import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { inEnterpriseViewSelector, isEnterpriseAdminSelector, reroutingProductSelector } from '../authentication/redux'
import React, { useCallback, useMemo, useState } from 'react'
import { ReroutingNumber, ServerPage } from '../app/types'
import { TitledBody } from '../common/components/TitledBody'
import ErrorAlert from '../common/components/ErrorAlert'
import { Sort, useInfiniteScroll } from '../common/components/table/useInfiniteScroll'
import { api, buildPaginatedUrl } from '../common/api'
import { useAccessHelpers } from '../common/util/hooks'
import { InfiniteScrollTable } from '../common/components/table/Table'
import { sorterHeader } from '../common/components/table/SorterHeader'
import useReroutingNumberForm from './useReroutingNumberForm'
import TableRowActionsMenu from '../common/components/table/TableRowActionsMenu'
import { format } from 'date-fns'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import CancelButton from '../common/form/CancelButton'
import ConfirmationModal from '../common/components/ConfirmationModal'
import { CheckCircleOutline, HighlightOff } from '@mui/icons-material'
import { ReroutingSearch } from './search'

export const Rerouting = () => {
    const { t } = useTranslation()
    const isEnterpriseAdmin = useSelector(isEnterpriseAdminSelector)
    const inEnterpriseView = useSelector(inEnterpriseViewSelector)
    const reroutingProduct = useSelector(reroutingProductSelector)
    const [numberToUpdate, setNumberToUpdate] = useState<ReroutingNumber | null>(null)
    const [numberToActivate, setNumberToActivate] = useState<ReroutingNumber | null>(null)
    const [numberToDeactivate, setNumberToDeactivate] = useState<ReroutingNumber | null>(null)
    const createUrl = useCallback((postfix) => `/api/enterprise/rerouting/${reroutingProduct?.productId}/${postfix}`, [reroutingProduct])
    const { hasWriteAccess } = useAccessHelpers()

    const isActive = (number: ReroutingNumber): boolean => {
        const now = new Date()
        return (number.activeFrom || number.activeTo || false) && (!number.activeFrom || number.activeFrom <= now) && (!number.activeTo || number.activeTo >= now)
    }

    const fetchNumbers = useCallback(
        async (pageParam = 0, sortParam: Sort, searchOptions: any | null): Promise<ServerPage<ReroutingNumber>> => {
            const url = buildPaginatedUrl(createUrl('number'), pageParam, sortParam, searchOptions?.searchTerm)
            const finalUrl = searchOptions === null || searchOptions.active === null ? url : `${url}&active=${searchOptions?.active}`
            const { data } = await api.get(finalUrl)
            if (data && Array.isArray(data.content)) {
                data.content = data.content.map((entry: any) => ({
                    ...entry,
                    activeFrom: entry.activeFrom ? new Date(entry.activeFrom) : null,
                    activeTo: entry.activeTo ? new Date(entry.activeTo) : null,
                }))
            }
            return data
        },
        [createUrl],
    )

    const { sort, setSort, setSearchTerm, rows, error, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteScroll<ReroutingNumber>({
        queryKey: `rerouting-${reroutingProduct?.uuid}`,
        initialSort: { field: 'startNumber', order: 'asc' },
        fetchFunction: fetchNumbers,
    })

    const onSubmit = useCallback(
        async (formData: ReroutingNumber) => {
            const url = createUrl(`number${numberToUpdate?.uuid ? `/${numberToUpdate.uuid}` : ''}`)
            await api.patch(url, formData)

            setNumberToUpdate(null)

            await refetch()
        },
        [refetch, setNumberToUpdate, numberToUpdate, createUrl],
    )

    const { handleSubmit, form: Form, submitButton: SubmitButton, reset, setValue } = useReroutingNumberForm({
        number: numberToUpdate,
        onSubmit,
        allowInternationalCallForwarding: reroutingProduct?.allowInternationalCallForwarding || false,
    })

    const activateNumber = useCallback(
        async (formData: ReroutingNumber | null) => {
            if (!formData) return
            const url = createUrl(`number${numberToActivate?.uuid ? `/${numberToActivate.uuid}` : ''}/activate`)
            await api.post(url, formData)

            setNumberToActivate(null)
        },
        [setNumberToActivate, numberToActivate, createUrl],
    )

    const deactivateNumber = useCallback(
        async (formData: ReroutingNumber | null) => {
            if (!formData) return
            const url = createUrl(`number${numberToDeactivate?.uuid ? `/${numberToDeactivate.uuid}` : ''}/deactivate`)
            await api.post(url, formData)

            setNumberToDeactivate(null)
        },
        [setNumberToDeactivate, numberToDeactivate, createUrl],
    )

    const numberForm = useMemo(
        () => (
            <form className={'ant-form ant-form-vertical'} onSubmit={handleSubmit} noValidate={true}>
                <Dialog disablePortal open={!!numberToUpdate} onClose={() => setNumberToUpdate(null)}>
                    <DialogTitle>{t('rerouting.number.update-dialog-title')}</DialogTitle>
                    <DialogContent>
                        <Form />
                    </DialogContent>
                    <DialogActions>
                        <SubmitButton />
                        <CancelButton onClick={() => setNumberToUpdate(null)} />
                    </DialogActions>
                </Dialog>
            </form>
        ),
        [Form, SubmitButton, numberToUpdate, handleSubmit, t],
    )

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: async (record: ReroutingNumber) => {
                    reset()
                    setNumberToUpdate(record)
                    setValue('destinationNumber', record.destinationNumber)
                    setValue('activeTo', record.activeTo)
                    setValue('activeFrom', record.activeFrom)
                    setValue('alias', record.alias)
                    setValue('comment', record.comment)
                    return false
                },
                dividerBeneath: false,
                needsWriteAccess: true,
            },
            {
                key: 'activate',
                text: t('rerouting.number.activate-now-button'),
                onClick: async (record: ReroutingNumber) => {
                    reset()
                    setNumberToActivate(record)
                    return false
                },
                dividerBeneath: false,
                needsWriteAccess: true,
                hide: (number: ReroutingNumber) => isActive(number) || !number.destinationNumber,
                icon: (props: object) => <CheckCircleOutline {...props} />,
            },
            {
                key: 'deactivate',
                text: t('rerouting.number.deactivate-now-button'),
                onClick: async (record: ReroutingNumber) => {
                    reset()
                    setNumberToDeactivate(record)
                    return false
                },
                dividerBeneath: false,
                needsWriteAccess: true,
                hide: (number: ReroutingNumber) => !isActive(number),
                icon: (props: object) => <HighlightOff {...props} />,
            },
        ],
        [t, setValue, reset],
    )

    // @ts-ignore
    const columns: ColumnDefinition<ReroutingNumber>[] = useMemo(
        () => [
            {
                title: t('rerouting.number.table.number'),
                key: 'number',
                sortField: 'startNumber',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('rerouting.number.table.destination'),
                key: 'destinationNumber',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('rerouting.number.table.active-from'),
                key: 'activeFrom',
                sorter: true,
                render: (date: Date | null) => <>{date ? format(date, 'dd/MM/yyyy HH:mm:ss') : ''}</>,
            },
            {
                title: t('rerouting.number.table.active-to'),
                key: 'activeTo',
                sorter: true,
                render: (date: Date | null) => <>{date ? format(date, 'dd/MM/yyyy HH:mm:ss') : ''}</>,
            },
            {
                title: t('rerouting.number.table.currently-active'),
                key: 'isActive',
                sorter: false,
                render: (_: any, record: ReroutingNumber) => <>{isActive(record) ? t('general.yes') : t('general.no')}</>,
            },
            {
                title: t('rerouting.number.table.alias'),
                key: 'alias',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('rerouting.number.table.comment'),
                key: 'comment',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            ...(!(inEnterpriseView && !isEnterpriseAdmin) ? [{
                    title: '',
                    key: 'uuid',
                    sorter: false,
                    render: (text: string, record: ReroutingNumber) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess} />,
                }] : []
            ),
        ],
        [t, rowActions, inEnterpriseView, isEnterpriseAdmin, hasWriteAccess],
    )

    const getRowKey = useCallback(entry => entry.uuid, [])
    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const onSearch = useCallback(async (searchTerm: any) => {
        setSearchTerm(searchTerm)
    }, [setSearchTerm])

    return <>
        <TitledBody title={t('navigation.rerouting')}>
            <ReroutingSearch onSearch={onSearch} />
            <ErrorAlert showAlert={!!error} />
            <InfiniteScrollTable<ReroutingNumber>
                hasNextPage={hasNextPage}
                data={rows}
                fetchNextPage={fetchNextPage}
                isFetching={isFetching}
                getRowKey={getRowKey}
                sorter={sorter}
                columns={columns}
                tableId={'reroutingNumberTable'}
            />
        </TitledBody>
        {numberForm}
        {numberToActivate && <ConfirmationModal<ReroutingNumber | null>
            item={numberToActivate}
            action={activateNumber}
            buttonLabel={t('rerouting.number.activate-now-button')}
            title={t('rerouting.number.activate-now-title')}
            text={(reroutingNumber) => t('rerouting.number.activate-now-text', { number: reroutingNumber?.number })}
            onClose={() => setNumberToActivate(null)}
            onSuccess={() => {
                refetch()
            }} />}
        {numberToDeactivate && <ConfirmationModal<ReroutingNumber | null>
            item={numberToDeactivate}
            action={deactivateNumber}
            buttonLabel={t('rerouting.number.deactivate-now-button')}
            title={t('rerouting.number.deactivate-now-title')}
            text={(reroutingNumber) => t('rerouting.number.deactivate-now-text', { number: reroutingNumber?.number })}
            onClose={() => setNumberToDeactivate(null)}
            onSuccess={() => {
                refetch()
            }} />}
    </>
}